// Which parts of a lab report's reading a clinician confirms, and what each needs before it can be
// (TGCLOUD-1868, plan D3 and D4; decisions 6, 13, 14 and 15). Pure: no React, no requests.
//
// THE GROUPS ARE THE SERVER'S. The intake (or hand-off) answer names them (`verification.groups`,
// backend services/testResults/confidence.js), each { key, kind, findingId?, paths, confident,
// reasons }, its paths the PROPOSAL's:
//   gene-list                            every gene row, and the list state (genesTestedList)
//   variant:<findingId>:identity         a variant's c., p., g., transcript, build and zygosity
//   variant:<findingId>:classification   its interpretation, with the lab's wording riding on it
//   overall-result                       the Test result the conclusion suggests (saved as testResult)
// The save refuses unless every one is confirmed exactly once (backend verification.js), so the
// view offers one Confirm per group and nothing that confirms more than one.
//
// A GROUP CAN BE CONFIRMED (groupNeeds returns nothing) when
//   - no contradiction between the report and the Test in it is left unchosen (a variant's
//     contradiction is both of its groups'; a gene row's the gene list's; the Test result's the
//     overall result's). Every other contradiction rides with the Test, and Save waits for it;
//   - every value the reader was not confident of (`lowConfidence`) holds one on the form: typed,
//     or chosen from a list with no default, IN THIS REVIEW, and compared with what was read. A
//     value the Test held before the report is not one: the application empties it
//     (applyProposal, `withheld`), and one held some other way waits until it is entered here. "Not
//     stated" chosen from a list is a value. A report variant's build must be GRCh37 or GRCh38 and
//     its transcript versioned, as the save requires. A variant the form no longer holds asks for
//     nothing: nothing of it is saved;
//   - for the gene list, its state is chosen and may be chosen (listStateOptions): never complete
//     for an exome or a genome (decision 13), nor with fewer genes than the report states (14),
//     nor with none (the save's GENES_TESTED_LIST_COMPLETE_EMPTY).
//
// A CONFIRMATION LASTS WHILE THE GROUP IS AS IT WAS CONFIRMED. groupSignature says what the form
// holds for the group; a confirmation made against one signature is gone once the signature
// differs, so editing any value of a confirmed group un-confirms it (useGroupConfirmations).
//
// ALSO HERE: the list state the application proposes as it fills the form (proposedListState:
// "complete" only for a list taken as printed), how a low-confidence value is entered
// (fieldKindOf), and the groups and values in words (groupNameOf, lowValueName), for the cards and
// for a refused save. useReadingChecks puts it together for the form.
//
// THE WORDS ARE IN THE SELECTED LANGUAGE (TGCLOUD-1893): each function that says a sentence takes the
// caller's `t` (useReadingChecks passes its own), defaulting to English. An option's `label` stays
// the English key, translated where it is shown; its `reason` is a sentence, said in `t`.

import { english } from 'src/i18n/language';
import {
  READING_WARNINGS,
  conflictsOf,
  variantLabel
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/applyProposal';
import { requiresIdentityAcknowledgement } from 'src/components/pedigree-chart/pedigree-sidebar/sections/test-results/labReportDisplay';
import {
  GENES_TESTED_LIST_LABELS,
  GENOME_BUILDS,
  ZYGOSITIES,
  geneKeyOf,
  genesTestedListStatusesFor,
  isVersionedTranscript,
  rowKeyOf
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/test-record/testRecordModel';
import {
  COMPLETE_NEEDS_A_GENE,
  GROUP_KIND_TEXT,
  PARTIAL_NEEDS_A_GENE,
  WAIT_FOR_REPORT,
  completeShortText
} from './labReportVerificationVocabulary';

export const GROUP_KINDS = Object.freeze({
  geneList: 'gene-list',
  identity: 'variant-identity',
  classification: 'variant-classification',
  overallResult: 'overall-result'
});
export const GENE_LIST_KEY = 'gene-list';
export const OVERALL_RESULT_KEY = 'overall-result';
export const identityKeyOf = (findingId) => `variant:${findingId}:identity`;
export const classificationKeyOf = (findingId) =>
  `variant:${findingId}:classification`;

// The fields of each variant group, on the form's variant (plan B1). A variant group's own `paths`
// name only what the report gave; an edit to any of these fields un-confirms it, and so does a
// change to the variant's gene for its identity (groupSignature; review of the view).
export const IDENTITY_FIELDS = Object.freeze([
  'variantC',
  'variantP',
  'variantG',
  'transcript',
  'genomeBuild',
  'zygosity'
]);
export const CLASSIFICATION_FIELDS = Object.freeze([
  'variantInterpretation',
  'labClassification.classificationDate'
]);

const isPlain = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const hasText = (v) => typeof v === 'string' && v.trim() !== '';
const getPath = (object, path) =>
  String(path || '')
    .split('.')
    .reduce((v, key) => (v == null ? undefined : v[key]), object);
const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const isCalendarDate = (value) => {
  const m = CALENDAR_DATE.exec(typeof value === 'string' ? value : '');
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === mo - 1 &&
    date.getUTCDate() === d
  );
};

/** The groups a reading names (its answer's `verification.groups`), in the answer's order. */
export const groupsOf = (reading) =>
  Array.isArray(reading?.verification?.groups)
    ? reading.verification.groups.filter(
        (g) => isPlain(g) && typeof g.key === 'string' && g.key
      )
    : [];

/** The variant on the form that is this finding of the report: the one carrying its findingId. */
export const formVariantOf = (form, findingId) =>
  (Array.isArray(form?.variants) ? form.variants : []).find(
    (v) => isPlain(v) && hasText(v.findingId) && v.findingId === findingId
  ) || null;

/**
 * Where a reading's path lands on the form:
 *   { kind: 'gene-list' }                          the gene rows, or the list state
 *   { kind: 'variant', findingId, field }          a value of the variant that is that finding
 *   { kind: 'field', field }                       a Test field (`testResultSuggestion` is testResult)
 * or null for a variant path the reading does not hold.
 */
export function placeOfPath(reading, path) {
  const text = typeof path === 'string' ? path : '';
  if (
    text === 'genesTested' ||
    text === 'genesTestedList' ||
    text.startsWith('genesTested.')
  ) {
    return { kind: 'gene-list' };
  }
  const m = /^variants\.(\d+)\.(.+)$/.exec(text);
  if (m) {
    const variant = (reading?.proposal?.variants || [])[Number(m[1])];
    return isPlain(variant) && hasText(variant.findingId)
      ? { kind: 'variant', findingId: variant.findingId, field: m[2] }
      : null;
  }
  if (
    text === 'testResultSuggestion' ||
    text.startsWith('testResultSuggestion.')
  ) {
    return { kind: 'field', field: 'testResult' };
  }
  return text ? { kind: 'field', field: text.split('.')[0] } : null;
}

/**
 * What the form holds at a reading's path: { found: true, value }, or { found: false } when the
 * finding it belongs to is not on the form (removed, or the Test kept its own variant).
 */
export function formValueAt(form, reading, path) {
  const place = placeOfPath(reading, path);
  if (!place) return { found: false };
  if (place.kind === 'variant') {
    const variant = formVariantOf(form, place.findingId);
    return variant
      ? { found: true, value: getPath(variant, place.field) }
      : { found: false };
  }
  if (place.kind === 'gene-list') {
    return { found: true, value: form?.genesTested };
  }
  return { found: true, value: form?.[place.field] };
}

/**
 * Does a value hold what a clinician typed or chose, for this field? Text that is not blank, a
 * code from its list ("not stated" included), a build the save accepts, a versioned transcript, a
 * calendar date. The server's isBlankSaved, with each field's own rule.
 */
export function holdsValue(field, value) {
  const last = String(field || '')
    .split('.')
    .pop();
  if (last === 'genomeBuild') return GENOME_BUILDS.includes(value);
  if (last === 'transcript') return isVersionedTranscript(value);
  if (last === 'zygosity') {
    return isPlain(value) && ZYGOSITIES.includes(value.value);
  }
  if (last === 'classificationDate') return isCalendarDate(value);
  if (typeof value === 'string') return value.trim() !== '';
  if (isPlain(value)) return hasText(value.value) || hasText(value.label);
  return false;
}

/**
 * The reading's low-confidence values, each with the group it belongs to and where it lands on the
 * form: [{ path, reason, readingDigest, printedDigest, groupKey, place }]. The gene list withheld
 * whole is `genesTested`, the gene list's.
 */
export function lowConfidenceOf(reading) {
  const groups = groupsOf(reading);
  const list = Array.isArray(reading?.lowConfidence)
    ? reading.lowConfidence
    : [];
  return list
    .filter((l) => isPlain(l) && typeof l.path === 'string' && l.path)
    .map((l) => {
      const group =
        l.path === 'genesTested'
          ? groups.find((g) => g.key === GENE_LIST_KEY)
          : groups.find(
              (g) => Array.isArray(g.paths) && g.paths.includes(l.path)
            );
      return {
        path: l.path,
        reason: typeof l.reason === 'string' ? l.reason : null,
        readingDigest:
          typeof l.readingDigest === 'string' ? l.readingDigest : null,
        printedDigest:
          typeof l.printedDigest === 'string' ? l.printedDigest : null,
        groupKey: group ? group.key : null,
        place: placeOfPath(reading, l.path)
      };
    });
}

/** The group keys a contradiction of the plan holds back; none for one that rides with the Test. */
export function conflictGroupKeys(item) {
  if (!isPlain(item)) return [];
  if (typeof item.id === 'string' && item.id.startsWith('variant:')) {
    return hasText(item.findingId)
      ? [identityKeyOf(item.findingId), classificationKeyOf(item.findingId)]
      : [];
  }
  if (hasText(item.key)) return [GENE_LIST_KEY];
  if (item.field === 'testResult') return [OVERALL_RESULT_KEY];
  return [];
}

const chosen = (choices, item) =>
  choices?.[item.id] === 'report' || choices?.[item.id] === 'current';

/** The plan's contradictions not chosen yet, and which of those ride with the Test (no group). */
export function openConflicts(plan, choices) {
  const open = plan ? conflictsOf(plan).filter((c) => !chosen(choices, c)) : [];
  return {
    open,
    riding: open.filter((c) => conflictGroupKeys(c).length === 0)
  };
}

/**
 * The list states the gene list card offers, each { value, label, disabled, reason }: never
 * `complete` for an exome or a genome scope (decision 13, not offered at all); `complete` disabled,
 * with the reason, with fewer gene rows than the report states (decision 14) or with none; and
 * `partial` disabled with none (`none` is what a list of no genes is).
 */
export function listStateOptions({
  testScope,
  rowCount = 0,
  statedCount = null,
  t = english
} = {}) {
  // Which states a Test of this scope may hold is testRecordModel's rule, bound to the backend's
  // (rules.SCOPES_WITHOUT_COMPLETE_GENE_LIST): a copy of its own here was bound to nothing.
  return genesTestedListStatusesFor(testScope?.kind).map((s) => {
    let reason = null;
    if (s === 'complete') {
      if (rowCount === 0) reason = t(COMPLETE_NEEDS_A_GENE);
      else if (Number.isFinite(statedCount) && rowCount < statedCount) {
        reason = completeShortText(statedCount, rowCount, t);
      }
    }
    if (s === 'partial' && rowCount === 0) reason = t(PARTIAL_NEEDS_A_GENE);
    return {
      value: s,
      label: GENES_TESTED_LIST_LABELS[s],
      disabled: !!reason,
      reason
    };
  });
}

/** Is `complete` left out for this scope (decision 13)? */
export const completeNotOffered = (testScope) =>
  !genesTestedListStatusesFor(testScope?.kind).includes('complete');

/** The list state the form holds, when the card may hold it (listStateOptions); else null. */
export function validListState(form, options) {
  const status = form?.genesTestedList?.status;
  return (options || []).some((o) => o.value === status && !o.disabled)
    ? status
    : null;
}

/** The number of genes the report states it analysed (geneList.stated), or null. */
export const statedCountOf = (reading) => {
  const count = reading?.geneList?.stated?.count;
  return Number.isFinite(count) ? count : null;
};

/** The list states the gene list card offers for this form and reading (listStateOptions). */
export const listOptionsFor = (form, reading, t = english) =>
  listStateOptions({
    testScope: form?.testScope,
    rowCount: Array.isArray(form?.genesTested) ? form.genesTested.length : 0,
    statedCount: statedCountOf(reading),
    t
  });

/**
 * Are the gene rows on the form the report's genes, no more and no fewer, by gene key? A list
 * withheld from the answer (the gate not ok) offers none, so no form holds it.
 */
export function rowsAreTheReports({ reading, form }) {
  const keysOf = (rows) =>
    new Set((Array.isArray(rows) ? rows : []).map(rowKeyOf).filter(Boolean));
  const report = keysOf(reading?.proposal?.genesTested);
  const held = keysOf(form?.genesTested);
  return (
    report.size > 0 &&
    report.size === held.size &&
    [...report].every((k) => held.has(k))
  );
}

/**
 * The list state the application proposes as it fills the form (plan B2, D3): `complete`, where
 * the report's gene list was taken as printed (its group confident: the gate ok, every row printed
 * as a list), the Test says nothing of its list yet, the rows the form now holds are exactly the
 * report's genes, and `complete` may be said of them (listStateOptions: never for an exome or a
 * genome, nor below the count the report states, nor with no gene). Otherwise null: a list entered
 * by hand is asked with no default, and a state the Test already holds is the Test's.
 *
 * "Exactly the report's genes": a gene the Test listed that the report does not (one entered when
 * the test was requested) is not a gene the laboratory analysed, so the merged rows are not "every
 * gene the laboratory analysed", and nothing is proposed for them (review of the view: one Confirm
 * saved the Test's BRCA1 as analysed by a five-gene Lynch panel).
 *
 * @param {{ reading: object, form: object }} args  `form` as the application left it
 * @returns {{ status: 'complete' } | null}
 */
export function proposedListState({ reading, form }) {
  const group = groupsOf(reading).find((g) => g.key === GENE_LIST_KEY);
  if (!group || group.confident !== true) return null;
  if (form?.genesTestedList?.status) return null;
  if (!rowsAreTheReports({ reading, form })) return null;
  const complete = listOptionsFor(form, reading).find(
    (o) => o.value === 'complete'
  );
  return complete && !complete.disabled ? { status: 'complete' } : null;
}

// The coded fields whose list has no "not stated" of its own, offered one where the reading holds
// no value of the list (its readingDigest null), and saved as no value: an interpretation, which
// the Test and the genotype copy have always held empty for a source that states none (backend
// verification.statesNoInterpretation). A zygosity's "not stated" is a code of its list.
export const NOT_STATED_FIELDS = Object.freeze(['variantInterpretation']);

// How a value left empty is asked for: chosen from a list, or typed as printed.
export const CODED_FIELDS = Object.freeze([
  'genomeBuild',
  'zygosity',
  'variantInterpretation',
  'testResult'
]);
const FIELD_TEXT = Object.freeze({
  variantC: 'the c. description',
  variantP: 'the p. description',
  variantG: 'the g. description',
  transcript: 'the versioned transcript',
  genomeBuild: 'the genome build',
  zygosity: 'the zygosity',
  variantInterpretation: 'the variant interpretation',
  'labClassification.classificationDate': 'the classification date',
  testResult: 'the Test result'
});
const isChosenField = (field) =>
  CODED_FIELDS.includes(field) || field.endsWith('classificationDate');
const fieldNeed = (field, t) => {
  const vars = { field: t(FIELD_TEXT[field] || 'each value left empty') };
  return isChosenField(field)
    ? t('Choose {{field}}.', vars)
    : t('Type {{field}} as the report prints it.', vars);
};
// A value entered, and not compared with what was read yet: typed and not left, chosen some other
// way than here, or its comparison still being worked out.
const comparingNeed = (field, t) =>
  t('Comparing {{field}} with what was read.', {
    field: t(FIELD_TEXT[field] || 'the value')
  });
const enterHereNeed = (field, t) => {
  const vars = { field: t(FIELD_TEXT[field] || 'the value') };
  return isChosenField(field)
    ? t('Choose {{field}} here, so it is compared with what was read.', vars)
    : t(
        'Finish typing {{field}}: it is compared with what was read when you leave the field.',
        vars
      );
};
export const CHOOSE_FIRST =
  'First choose the Test’s value or the report’s, under “Where the report differs from this Test”.';
export const CHOOSE_LIST_STATE = 'Choose what this list of genes is.';

/**
 * How a low-confidence value is entered on the form: 'coded' a select with no default, 'date' a
 * calendar date picked, 'text' typed as the report prints it.
 */
export const fieldKindOf = (field) => {
  if (String(field || '').endsWith('classificationDate')) return 'date';
  return CODED_FIELDS.includes(field) ? 'coded' : 'text';
};

// The reading's own variant that a proposal path is about, as a clinician names it.
const readVariantName = (reading, findingId) =>
  variantLabel(
    (reading?.proposal?.variants || []).find(
      (v) => isPlain(v) && v.findingId === findingId
    )
  );

/**
 * A group in words, by the reading's own variant: "Gene list", "Variant MLH1 c.1852_1854del",
 * "Laboratory classification of MLH1 c.1852_1854del", "Overall result".
 */
export function groupNameOf(group, reading, t = english) {
  const kind = t(GROUP_KIND_TEXT[group?.kind] || 'Part of the report');
  if (
    group?.kind !== GROUP_KINDS.identity &&
    group?.kind !== GROUP_KINDS.classification
  ) {
    return kind;
  }
  const named = readVariantName(reading, group.findingId);
  if (!named) return kind;
  return group.kind === GROUP_KINDS.identity
    ? t('Variant {{named}}', { named })
    : t('{{kind}} of {{named}}', { kind, named });
}

/**
 * A value the reader was not confident of, in words: "the genome build of MLH1 c.1852_1854del",
 * "the Test result", "the gene list". Null for a path the reading does not place.
 */
export function lowValueName(reading, path, t = english) {
  if (path === 'genesTested') return t('the gene list');
  const place = placeOfPath(reading, path);
  if (!place) return null;
  if (place.kind === 'variant') {
    const text = t(FIELD_TEXT[place.field] || 'a value');
    const named = readVariantName(reading, place.findingId);
    return named ? t('{{field}} of {{named}}', { field: text, named }) : text;
  }
  if (place.kind === 'gene-list') return t('the gene list');
  return FIELD_TEXT[place.field] ? t(FIELD_TEXT[place.field]) : null;
}

/**
 * What a group still needs before it can be confirmed, one sentence each; empty when it can be.
 *
 * @param {object} args
 * @param {object} args.group        one of the reading's groups
 * @param {object} args.form         the Test form's values now
 * @param {object} args.reading      the intake (or hand-off) answer
 * @param {object} [args.plan]       the application's plan (applyProposal.planApplication)
 * @param {object} [args.choices]    the review's choices per contradiction
 * @param {object[]} [args.listOptions]  listStateOptions, for the gene list
 * @param {(path: string, value) => 'compared'|'comparing'|'not-entered'} [args.comparison]
 *   whether the value a low-confidence path holds was entered in this review and compared with
 *   what was read (useReadingChecks). A value is counted only once it has been: one the form holds
 *   some other way (put there before the report, or not left yet) satisfies nothing, so no group
 *   is confirmed on a value nobody compared with the reading (plan B1, D3). Left out, every value
 *   held counts, as a pure caller with no review needs
 * @param {(path: string) => boolean} [args.notStated]  "Not stated" was chosen at this path where
 *   its list has no such code (NOT_STATED_FIELDS): no value on the form there is that value
 * @returns {string[]}
 */
export function groupNeeds({
  group,
  form,
  reading,
  plan = null,
  choices = {},
  listOptions = [],
  comparison = null,
  notStated = null,
  t = english
}) {
  if (!isPlain(group)) return [];
  const needs = [];
  const { open } = openConflicts(plan, choices);
  if (open.some((c) => conflictGroupKeys(c).includes(group.key))) {
    needs.push(t(CHOOSE_FIRST));
  }
  if (group.kind === GROUP_KINDS.geneList) {
    const status = form?.genesTestedList?.status;
    const option = (listOptions || []).find((o) => o.value === status);
    if (!option) needs.push(t(CHOOSE_LIST_STATE));
    // listStateOptions says the reason in the `t` it was given.
    else if (option.disabled) needs.push(option.reason);
    return needs;
  }
  for (const low of lowConfidenceOf(reading)) {
    if (low.groupKey !== group.key || !low.place) continue;
    const at = formValueAt(form, reading, low.path);
    if (!at.found) continue;
    const { field } = low.place;
    if (!holdsValue(field, at.value)) {
      const chosenNone =
        NOT_STATED_FIELDS.includes(field) &&
        !low.readingDigest &&
        typeof notStated === 'function' &&
        notStated(low.path);
      if (!chosenNone) needs.push(fieldNeed(field, t));
      continue;
    }
    const state =
      typeof comparison === 'function'
        ? comparison(low.path, at.value)
        : 'compared';
    if (state === 'comparing') needs.push(comparingNeed(field, t));
    else if (state !== 'compared') needs.push(enterHereNeed(field, t));
  }
  return needs;
}

/**
 * What the Test held where the application emptied a value the reader was not confident of, by the
 * reading's path (`testResultSuggestion`, `variants.0.genomeBuild`): applyProposal's `withheld`
 * field items and each variant's `cleared`. Said beside the empty field, "This Test held: …", so the
 * clinician can choose it again if the report says so.
 *
 * @returns {Map<string, *>}
 */
export function heldBeforeOf(plan, reading) {
  const out = new Map();
  if (!isPlain(plan)) return out;
  for (const item of Array.isArray(plan.fields) ? plan.fields : []) {
    if (
      isPlain(item) &&
      item.kind === 'withheld' &&
      item.field === 'testResult' &&
      hasText(item.current)
    ) {
      out.set('testResultSuggestion', item.current);
    }
  }
  const at = new Map(
    (reading?.proposal?.variants || []).map((v, i) => [v && v.findingId, i])
  );
  for (const item of Array.isArray(plan.variants) ? plan.variants : []) {
    if (!isPlain(item) || !Array.isArray(item.cleared)) continue;
    const i = at.get(item.findingId);
    if (i === undefined) continue;
    for (const c of item.cleared) {
      if (isPlain(c) && typeof c.field === 'string') {
        out.set(`variants.${i}.${c.field}`, c.value);
      }
    }
  }
  return out;
}

// A value as the signature reads it: keys in order, text trimmed, nothing for a blank.
function stable(value) {
  if (typeof value === 'string') return value.trim() || null;
  if (Array.isArray(value)) return value.map(stable);
  if (value instanceof Date) return value.toISOString();
  if (isPlain(value)) {
    const out = {};
    for (const key of Object.keys(value).sort()) {
      const v = stable(value[key]);
      if (v !== null && v !== undefined) out[key] = v;
    }
    return out;
  }
  return value === undefined ? null : value;
}

/**
 * What the form holds for a group, as one string: a confirmation lasts while it is unchanged.
 * The gene list is its rows and its state, and whether that state may be held; a variant group the
 * values of its fields on the variant that is its finding ('absent' when the form holds none); the
 * overall result the Test result.
 */
export function groupSignature(group, { form, listOptions = [] } = {}) {
  if (!isPlain(group)) return '';
  if (group.kind === GROUP_KINDS.geneList) {
    return JSON.stringify(
      stable({
        rows: Array.isArray(form?.genesTested) ? form.genesTested : [],
        status: form?.genesTestedList?.status || null,
        held: validListState(form, listOptions) !== null
      })
    );
  }
  if (
    group.kind === GROUP_KINDS.identity ||
    group.kind === GROUP_KINDS.classification
  ) {
    const variant = formVariantOf(form, group.findingId);
    if (!variant) return 'absent';
    const identity = group.kind === GROUP_KINDS.identity;
    const fields = identity ? IDENTITY_FIELDS : CLASSIFICATION_FIELDS;
    return JSON.stringify(
      stable({
        ...Object.fromEntries(fields.map((f) => [f, getPath(variant, f)])),
        // A c. description means nothing under another gene: the variant's gene, by its key (the
        // lookup's label and its symbol are one gene), is part of what was confirmed.
        ...(identity ? { gene: geneKeyOf(variant.gene) || null } : {})
      })
    );
  }
  if (group.kind === GROUP_KINDS.overallResult) {
    return JSON.stringify(stable({ testResult: form?.testResult ?? null }));
  }
  return '';
}

/**
 * The reading's findings that are not on the form (removed, kept as the Test's own variant, or
 * waiting for a choice): their groups are still confirmed, and the save records nothing of them.
 * [{ findingId, variant (as read), groups: [identity, classification] }], in the reading's order.
 */
export function findingsNotOnForm({ reading, form }) {
  const groups = groupsOf(reading);
  const out = [];
  for (const variant of reading?.proposal?.variants || []) {
    if (!isPlain(variant) || !hasText(variant.findingId)) continue;
    if (formVariantOf(form, variant.findingId)) continue;
    const own = groups.filter(
      (g) =>
        (g.kind === GROUP_KINDS.identity ||
          g.kind === GROUP_KINDS.classification) &&
        g.findingId === variant.findingId
    );
    if (own.length)
      out.push({ findingId: variant.findingId, variant, groups: own });
  }
  return out;
}

/**
 * Why Save waits, one sentence each, in the order shown: a part not confirmed, an
 * acknowledgement not made, a contradiction that rides with the Test not chosen. Empty when the
 * Test can be saved with the report.
 */
export function saveBlockers({
  groups = [],
  isConfirmed = () => false,
  identity = null,
  acknowledged = false,
  warnings = [],
  acknowledgedWarnings = new Set(),
  needsUnverifiable = false,
  unverifiableAcknowledged = false,
  waitingForReport = false,
  ridingConflicts = [],
  t = english
} = {}) {
  const out = [];
  // A report never drawn was never checked against (hazard 1868-H7).
  if (waitingForReport) out.push(t(WAIT_FOR_REPORT));
  const left = groups.filter((g) => !isConfirmed(g.key)).length;
  if (left) {
    out.push(
      left === 1
        ? t(
            'Confirm each part of the report: {{count}} is not confirmed yet.',
            {
              count: left
            }
          )
        : t(
            'Confirm each part of the report: {{count}} are not confirmed yet.',
            { count: left }
          )
    );
  }
  // Only a real disagreement needs the acknowledgement (decision 29): information alone never does.
  if (requiresIdentityAcknowledgement(identity) && !acknowledged) {
    out.push(
      t('Acknowledge that you have checked this is this person’s report.')
    );
  }
  for (const key of warnings) {
    if (!acknowledgedWarnings.has(key) && READING_WARNINGS[key]) {
      out.push(t(READING_WARNINGS[key].blocker));
    }
  }
  if (needsUnverifiable && !unverifiableAcknowledged) {
    out.push(
      t(
        'Say that you have checked these values against the report another way.'
      )
    );
  }
  const n = ridingConflicts.length;
  if (n) {
    out.push(
      n === 1
        ? t(
            'Choose the Test’s value or the report’s for {{count}} difference.',
            {
              count: n
            }
          )
        : t(
            'Choose the Test’s value or the report’s for {{count}} differences.',
            { count: n }
          )
    );
  }
  return out;
}

/** labReportApplication.confirmations: each confirmed group once, with when it was confirmed. */
export const confirmationsOf = (groups, confirmedAt) =>
  (Array.isArray(groups) ? groups : [])
    .filter((g) => !!confirmedAt(g.key))
    .map((g) => ({ group: g.key, at: confirmedAt(g.key) }));
